<template>  <div class="bg-white/90 rounded-xl shadow-lg overflow-hidden will-change-transform transition-colors"
  :class="{'opacity-75 expired-cruise': cruise.expired}">
    <div class="border-b border-gray-200 bg-primary-100/30 p-3 sm:p-4 lg:p-6">
      <!-- Mobile-optimized header -->
      <div class="space-y-3 sm:space-y-4">
        <!-- Title and status -->
        <div class="flex flex-col space-y-2">
          <h2 class="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 break-words leading-tight">
            {{ cruise.name }}
          </h2>
          <span v-if="cruise.expired" class="self-start inline-flex items-center gap-1 text-xs font-medium px-2 py-1 bg-red-100 text-red-600 rounded-full">
            <v-icon size="14">mdi-alert-circle</v-icon>
            Expired
          </span>
        </div>
          <!-- Mobile-first action buttons - Card-based layout -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <!-- Primary Actions Card -->
          <div class="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 border border-blue-100">
            <h4 class="text-xs font-semibold text-blue-800 mb-2 flex items-center">
              <v-icon size="14" class="mr-1">mdi-cog</v-icon>
              Management
            </h4>
            <div class="space-y-2">              <v-btn
                color="success"
                variant="tonal"
                prepend-icon="mdi-message"
                @click="$emit('open-messages', cruise.id)"
                :class="{'pulsate-message': hasNewMessages}"
                class="w-full transition-all relative"
                size="small"
              >
                  <span class="hidden xs:inline">Messages</span>
                  <span class="xs:hidden">Msg</span>
                <v-badge
                  v-if="cruise.unreadMessageCount && cruise.unreadMessageCount > 0"
                  :content="cruise.unreadMessageCount"
                  color="error"
                  offset-x="-5"
                  offset-y="-5"
                ></v-badge>
              </v-btn>
            </div>
          </div>
          
          <!-- Tools & Actions Card -->
          <div class="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-3 border border-orange-100">
            <h4 class="text-xs font-semibold text-orange-800 mb-2 flex items-center">
              <v-icon size="14" class="mr-1">mdi-tools</v-icon>
              Tools
            </h4>
            <div class="space-y-2">
              <v-btn
                color="info"
                variant="tonal"
                prepend-icon="mdi-qrcode"
                :loading="isDownloadingQR"
                @click="handleQrCodeDownload"
                class="w-full transition-all"
                size="small"
              >
                <span class="hidden lg:inline">Download </span>QR Codes
              </v-btn>
              
              <v-btn
                color="error"
                variant="tonal"
                prepend-icon="mdi-refresh"
                :loading="isResetting"
                :disabled="cruise.expired"
                @click="$emit('reset', cruise.id)"
                class="w-full transition-all"
                size="small"
              >
                <span class="hidden sm:inline">Hide All Ducks</span>
                <span class="sm:hidden">Hide Ducks</span>
              </v-btn>
            </div>
          </div>
        </div>
      </div><!-- Package Information with mobile-first styling -->
      <div v-if="cruise.package" class="mt-3 sm:mt-4 p-3 sm:p-4 bg-blue-50/80 rounded-lg border border-blue-100 shadow-sm">
        <div class="space-y-3 sm:space-y-0 sm:flex sm:justify-between sm:items-start">
          <div class="flex-1 min-w-0">
            <span class="text-sm font-medium text-blue-800 flex items-center">
              <v-icon size="16" color="blue" class="mr-1">mdi-package-variant</v-icon>
              {{ cruise.package.name }} Package
            </span>            <div class="flex items-center mt-2 gap-2">
              <span class="text-xs sm:text-sm text-gray-600 flex items-center flex-shrink-0">
                <v-icon size="14" color="gray" class="mr-1">mdi-duck</v-icon>
                {{ actualDucksCreated }} ducks
              </span>
              <div class="relative flex-1 max-w-20 sm:max-w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  class="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full transition-all"
                  :style="`width: ${Math.min(100, (actualDucksCreated / maxDucksAllowed) * 100)}%`"
                ></div>
              </div>
            </div>
          </div>
          
          <div class="flex flex-wrap gap-2">
            <v-btn
              v-if="cruise.package.allowsAdditionalDucks"
              color="success"
              size="small"
              prepend-icon="mdi-duck"
              variant="tonal"
              class="font-medium text-xs"
              @click="$emit('buy-more-ducks', cruise.id)"
            >
              <span class="hidden sm:inline">Buy More </span>Ducks
            </v-btn>            
            <v-btn
              v-if="isDuckLimitReached"
              color="primary"
              size="small"
              prepend-icon="mdi-rocket-launch"
              variant="tonal"
              class="font-medium text-xs"
              @click="$emit('upgrade', cruise.id)"
            >
              Upgrade
            </v-btn>
          </div>
        </div>
        
        <!-- Tracking Information - mobile optimized -->
        <div v-if="cruise.package && !cruise.package.allowsAdditionalDucks && cruise.tracking" 
             class="mt-3 pt-3 border-t border-blue-200">
          <h4 class="text-xs sm:text-sm font-medium text-blue-800 mb-2 flex items-center">
            <v-icon size="14" color="blue" class="mr-1">mdi-truck-fast</v-icon>
            Shipping Status
          </h4>
          <div class="flex items-center p-2 bg-white/60 rounded-md gap-2">
            <v-icon class="flex-shrink-0" size="18" :color="getTrackingStatusColor(cruise.tracking.status)">
              {{ getTrackingStatusIcon(cruise.tracking.status) }}
            </v-icon>
            <div class="min-w-0 flex-1">
              <div class="text-xs sm:text-sm font-semibold text-gray-800">
                {{ getTrackingStatusLabel(cruise.tracking.status) }}
              </div>
              <div v-if="cruise.tracking.trackingNumber" class="text-xs text-gray-600 mt-1 break-all">
                #{{ cruise.tracking.trackingNumber }}
              </div>
              <div class="text-xs text-gray-600">
                {{ formatTrackingDate(cruise.tracking.lastUpdated) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Cruise Details Section - mobile-first layout -->
      <div class="mt-4 sm:mt-5 space-y-3 sm:space-y-4 text-gray-600">
        <!-- Dates row -->
        <div class="grid grid-cols-1 xs:grid-cols-2 gap-3 sm:gap-6">
          <div class="cruise-detail-item">
            <p class="font-semibold flex items-center text-gray-800 text-xs sm:text-sm">
              <v-icon size="14" color="gray" class="mr-2">mdi-calendar-start</v-icon>
              Start
            </p>
            <p class="text-xs sm:text-sm mt-1">{{ formatDate(cruise.startDate) }}</p>
          </div>
          <div class="cruise-detail-item">
            <p class="font-semibold flex items-center text-gray-800 text-xs sm:text-sm">
              <v-icon size="14" color="gray" class="mr-2">mdi-calendar-end</v-icon>
              End
            </p>
            <p class="text-xs sm:text-sm mt-1">{{ formatDate(cruise.endDate) }}</p>
          </div>
        </div>        <!-- Details grid - responsive -->
        <div class="space-y-3 sm:space-y-0 sm:grid sm:grid-cols-1 lg:grid-cols-2 sm:gap-4">
          <!-- Editable About Us -->
          <div class="cruise-detail-item">
            <p class="font-semibold flex items-center text-gray-800 text-xs sm:text-sm mb-1">
              <v-icon size="14" color="gray" class="mr-2">mdi-information</v-icon>
              About Us
            </p>
            <v-textarea
              v-if="isEditingInfo"
              v-model="editableInfo.aboutUs"
              variant="outlined"
              density="compact"
              rows="3"
              auto-grow
              placeholder="Tell users about yourself and why you're hiding ducks..."
              class="text-xs"
            />
            <p v-else class="whitespace-pre-line break-words overflow-auto max-h-16 sm:max-h-20 text-xs sm:text-sm leading-relaxed pr-1">
              {{ cruise.aboutUs || 'No description provided' }}
            </p>
          </div>
          
          <!-- Editable Social Link -->
          <div class="cruise-detail-item">
            <p class="font-semibold flex items-center text-gray-800 text-xs sm:text-sm mb-1">
              <v-icon size="14" color="gray" class="mr-2">mdi-link</v-icon>
              Social Link
            </p>
            <v-text-field
              v-if="isEditingInfo"
              v-model="editableInfo.socialLink"
              variant="outlined"
              density="compact"
              placeholder="https://your-social-media-link.com"
              class="text-xs"
            />
            <a 
              v-else-if="cruise.socialLink"
              :href="cruise.socialLink" 
              target="_blank" 
              class="text-primary hover:underline break-all text-xs sm:text-sm"
            >
              {{ cruise.socialLink }}
            </a>
            <p v-else class="text-xs sm:text-sm text-gray-500">No social link provided</p>
          </div>
        </div>

        <!-- Edit Actions -->
        <div class="mt-4 flex gap-2">
          <v-btn
            v-if="!isEditingInfo"
            color="primary"
            variant="tonal"
            size="small"
            prepend-icon="mdi-pencil"
            @click="startEditingInfo"
          >
            Edit Info
          </v-btn>
          <template v-else>
            <v-btn
              color="success"
              variant="elevated"
              size="small"
              prepend-icon="mdi-content-save"
              :loading="savingInfo"
              @click="saveInfo"
            >
              Save
            </v-btn>
            <v-btn
              color="error"
              variant="tonal"
              size="small"
              prepend-icon="mdi-close"
              @click="cancelEditingInfo"
            >
              Cancel
            </v-btn>
          </template>
          <v-btn
            color="info"
            variant="tonal"
            size="small"
            prepend-icon="mdi-eye"
            @click="$emit('preview', cruise)"
          >
            Preview QR code view
          </v-btn>
        </div>
            </div>
          </div>
          
    <div class="p-4 sm:p-6">
      <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
        <h3 class="text-lg font-semibold text-gray-900  flex items-center">
          <v-icon size="22" color="amber-darken-2" class="mr-2">mdi-duck</v-icon>
          Ducks in Fleet: {{ cruise.ducks.length }}
          <span v-if="maxDucksAllowed" class="text-sm text-gray-500  ml-2">
            (Limit: {{ maxDucksAllowed }})
                </span>
        </h3>        <div class="flex gap-3">
          <v-btn
            v-if="cruise.ducks.length > 0"
            color="secondary"
            variant="tonal"
            prepend-icon="mdi-format-list-bulleted"
            @click="$emit('toggle-ducks', cruise.id)"
            class="font-medium transition-all"
          >
            {{ isDuckListVisible ? 'Hide Ducks' : 'Show Ducks' }}
          </v-btn>
        </div>
      </div>

      <!-- Empty state when no ducks exist -->
      <div v-if="cruise.ducks.length === 0 && !cruise.expired" class="text-center py-8 bg-blue-50/50 dark:bg-blue-900/10 rounded-lg border border-blue-100 dark:border-blue-800/30">
        <img src="/ducksImages/duckIcon.webp" class="w-16 h-16 mx-auto mb-4 opacity-50" alt="Empty duck" />
        <p class="text-gray-600 dark:text-gray-300 mb-2">No ducks created yet</p>
        <p class="text-sm text-gray-500 dark:text-gray-400">Create your first duck to start your expedition</p>
      </div>

      <duck-grid 
        v-else-if="isDuckListVisible"        :ducks="cruise.ducks" 
        :cruise-id="cruise.id"
        @duck-click="$emit('duck-details', $event)"
        @message-click="handleDuckMessageClick"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineProps, defineEmits, computed, ref, reactive } from 'vue';
import type { DetailedCruise, TrackingStatus } from '@/interfaces/cruise';
import DuckGrid from './DuckGrid.vue';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useDemoCruiseStore } from '@/stores/demoCruiseStore';

dayjs.extend(utc);

const demoCruiseStore = useDemoCruiseStore();

const props = defineProps({
  cruise: {
    type: Object as () => DetailedCruise,
    required: true
  },
  isDuckListVisible: {
    type: Boolean,
    default: false
  },
  isResetting: {
    type: Boolean,
    default: false
  },
  isDownloadingQR: {
    type: Boolean,
    default: false
  },
  hasNewMessages: {
    type: Boolean,
    default: false
  },
  maxDucksAllowed: {
    type: Number,
    default: 0
  },
  ducksCreated: {
    type: Number,
    default: 0
  }
});

const emit = defineEmits([
  'edit', 
  'preview',
  'cruise-updated',
  'reset', 
  'download-qr', 
  'open-messages', 
  'toggle-ducks', 
  'add-duck-with-warning',
  'duck-details',
  'duck-messages',
  'upgrade',
  'buy-more-ducks'
]);

// Editing state
const isEditingInfo = ref(false);
const savingInfo = ref(false);

// Editable data
const editableInfo = reactive({
  aboutUs: '',
  socialLink: ''
});

// Functions for editing
const startEditingInfo = () => {
  editableInfo.aboutUs = props.cruise.aboutUs || '';
  editableInfo.socialLink = props.cruise.socialLink || '';
  isEditingInfo.value = true;
};

const cancelEditingInfo = () => {
  isEditingInfo.value = false;
  // Reset editable data
  editableInfo.aboutUs = '';
  editableInfo.socialLink = '';
};

const saveInfo = async () => {
  try {
    savingInfo.value = true;
    
    // Simulate API call delay for demo purposes
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Update the cruise in the demo store
    const updateData = {
      id: props.cruise.id,
      aboutUs: editableInfo.aboutUs,
      socialLink: editableInfo.socialLink
    };

    const success = demoCruiseStore.updateCruise(updateData);
    
    if (success) {
      // Emit updated cruise data to parent
      emit('cruise-updated', {
        ...props.cruise,
        aboutUs: editableInfo.aboutUs,
        socialLink: editableInfo.socialLink
      });
      
      isEditingInfo.value = false;
      console.log('Cruise information updated successfully');
    } else {
      console.error('Failed to update cruise in demo store');
    }
  } catch (error) {
    console.error('Error updating cruise information:', error);
    // Handle error (could show error message)
  } finally {
    savingInfo.value = false;
  }
};

const formatDate = (dateString: string) => {
  // Use dayjs with UTC to preserve the exact date from ISO format
  return dayjs.utc(dateString).format('MMMM D, YYYY');
};

// Computed property to check if duck limit is reached
const isDuckLimitReached = computed(() => {
  return props.ducksCreated >= props.maxDucksAllowed && props.maxDucksAllowed > 0;
});

// Add this computed property to ensure consistent duck counting
const actualDucksCreated = computed(() => {
  // First try to use the passed prop if available
  if (props.ducksCreated && props.ducksCreated > 0) {
    return props.ducksCreated;
  }
  // Otherwise use the cruise's ducks array length as fallback
  return props.cruise.ducks.length;
});

// Helper functions for tracking status
const getTrackingStatusLabel = (status: TrackingStatus): string => {
  switch (status) {
    case 'RECEIVED': return 'Order Received';
    case 'PREPARING': return 'Preparing for Shipment';
    case 'SHIPPED': return 'Shipped';
    default: return 'Unknown Status';
  }
};

const getTrackingStatusIcon = (status: TrackingStatus): string => {
  switch (status) {
    case 'RECEIVED': return 'mdi-package-variant';
    case 'PREPARING': return 'mdi-package-variant-closed';
    case 'SHIPPED': return 'mdi-truck-delivery';
    default: return 'mdi-help-circle';
  }
};

const getTrackingStatusColor = (status: TrackingStatus): string => {
  switch (status) {
    case 'RECEIVED': return 'blue';
    case 'PREPARING': return 'orange';
    case 'SHIPPED': return 'green';
    default: return 'grey';
  }
};

const formatTrackingDate = (dateString: string): string => {
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Add a new method to handle QR code download button clicks
const handleQrCodeDownload = () => {
  // Check if the cruise has any ducks
  if (props.cruise.ducks.length === 0) {
    // Emit a special event that will redirect to AddDuckForm with a warning
    emit('add-duck-with-warning', props.cruise.id, 'Please create at least one duck before downloading QR codes.');
  } else {
    // If there are ducks, proceed with normal download
    emit('download-qr', props.cruise.id);
  }
};

const handleDuckMessageClick = (duckUuid: string) => {
  emit('duck-messages', duckUuid);
};
</script>

<style scoped>
.pulsate-message {
  animation: pulsate 1.5s ease-out infinite;
  position: relative;
  z-index: 1;
}

@keyframes pulsate {
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
}

/* Scrollbar styles */
.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: rgba(155, 155, 155, 0.5) transparent;
}

.scrollbar-thin::-webkit-scrollbar {
  width: 4px;
}

.scrollbar-thin::-webkit-scrollbar-track {
  background: transparent;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
  background-color: rgba(155, 155, 155, 0.5);
  border-radius: 10px;
}

.expired-cruise {
  position: relative;
}

.expired-cruise::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: repeating-linear-gradient(
    45deg,
    rgba(255, 0, 0, 0.05),
    rgba(255, 0, 0, 0.05) 10px,
    rgba(255, 0, 0, 0.1) 10px,
    rgba(255, 0, 0, 0.1) 20px
  );
  pointer-events: none;
}

.cruise-detail-item {
  transition: transform 0.2s;
}

.cruise-detail-item:hover {
  transform: translateY(-2px);
}

/* Button hover improvements */
:deep(.v-btn.transition-all) {
  transition: all 0.3s ease !important;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05) !important;
}

:deep(.v-btn.transition-all:hover) {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0,0,0,0.1) !important;
}

/* Add styles for the glowing button when no ducks exist */
.primary-glow {
  animation: glow 2s ease-in-out infinite alternate;
  box-shadow: 0 0 15px rgba(var(--v-theme-primary-rgb), 0.5);
}

@keyframes glow {
  from {
    box-shadow: 0 0 5px rgba(var(--v-theme-primary-rgb), 0.5);
  }
  to {
    box-shadow: 0 0 20px rgba(var(--v-theme-primary-rgb), 0.8);
  }
}

.scale-btn:hover {
  transform: translateY(-2px) scale(1.03);
}

/* Mobile-first responsive optimizations */
@media (max-width: 640px) {
  .cruise-detail-item {
    padding: 0.75rem 0;
  }
  
  .v-btn {
    font-size: 0.8125rem;
  }
  
  .v-btn--size-small {
    min-height: 36px;
  }
  
  /* Better text scaling for mobile */
  .text-lg {
    font-size: 1rem;
  }
  
  .text-xl {
    font-size: 1.125rem;
  }
}

@media (max-width: 475px) {
  .cruise-detail-item {
    padding: 0.5rem 0;
  }
  
  /* More compact buttons on very small screens */
  .v-btn {
    font-size: 0.75rem;
    padding: 0 8px;
  }
  
  .v-btn--size-small {
    min-height: 32px;
  }
}

/* Enhanced card hover effects */
.bg-gradient-to-r {
  transition: all 0.3s ease;
}

.bg-gradient-to-r:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Progress ring animation */
.text-amber-500 {
  transition: stroke-dashoffset 0.5s ease;
}

/* Duck fleet header animation */
@keyframes duck-bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
}

.bg-gradient-to-br:hover {
  animation: duck-bounce 0.6s ease;
}

/* Button elevation effects */
.elevation-4 {
  transition: all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1);
}

.elevation-4:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

/* Enhanced empty state */
.border-dashed {
  border-style: dashed;
  animation: dash 20s linear infinite;
}

@keyframes dash {
  to {
    stroke-dashoffset: -100;
  }
}

@media (prefers-reduced-motion: reduce) {
  .pulsate-message {
    animation: none;
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.7);
  }
  
  :deep(.v-btn.transition-all:hover) {
    transform: none;
  }
  
  .cruise-detail-item:hover {
    transform: none;
  }
  
  .bg-gradient-to-r:hover {
    transform: none;
  }
    .bg-gradient-to-br:hover {
    animation: none;
  }
    .primary-glow {
    animation: none;
    box-shadow: 0 0 10px rgba(var(--v-theme-primary-rgb), 0.6);
  }
}

/* Enhanced message button styles */
.message-btn-enhanced {
  overflow: hidden;
  position: relative;
  background: linear-gradient(to right, #4caf50, #2e7d32) !important;
  color: white !important;
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 3px 5px rgba(0, 0, 0, 0.1);
}

.message-btn-enhanced:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 8px rgba(0, 0, 0, 0.15);
}

.message-btn-enhanced:active {
  transform: translateY(0);
  box-shadow: 0 2px 3px rgba(0, 0, 0, 0.1);
}

.message-icon-container {
  position: relative;
}

.message-notification-dot {
  position: absolute;
  top: -5px;
  right: -5px;
  color: #ff5252;
  font-size: 12px;
  animation: pulse-dot 1.5s cubic-bezier(0.455, 0.03, 0.515, 0.955) infinite;
}

.message-badge {
  animation: bump 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;
}

.pulsate-message {
  animation: message-glow 2s ease-in-out infinite alternate;
  position: relative;
}

.pulsate-message::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 8px;
  z-index: -1;
  background: radial-gradient(circle, rgba(76, 175, 80, 0.3) 0%, rgba(76, 175, 80, 0) 70%);
  animation: ripple 2s linear infinite;
}

@keyframes pulse-dot {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.3);
  }
}

@keyframes message-glow {
  0% {
    box-shadow: 0 0 5px rgba(76, 175, 80, 0.5);
  }
  100% {
    box-shadow: 0 0 15px rgba(76, 175, 80, 0.8);
  }
}

@keyframes ripple {
  0% {
    transform: scale(1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
}

@keyframes bump {
  0% {
    transform: scale(0);
  }
  70% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}
</style>